/**
 * PromotionListSection
 *
 * SCRUM-1286: Guide Dashboard — Promotion Lifecycle UI
 * Displays existing promotions in Active/Scheduled/Past tabs with lifecycle actions:
 * activate, pause, resume, end early (with confirmation), edit (scheduled only), duplicate.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Tag, Loader2, AlertCircle, Play, Pause, Square, Copy, Pencil, RotateCcw } from 'lucide-react';
import { getPromotions, updatePromotion, createPromotion } from '../../../services/guidePricingApi';
import { formatCentsToDollars } from '../../../utils/formHelpers';

const TABS = [
  { key: 'active', label: 'Active', statuses: ['active', 'paused'] },
  { key: 'scheduled', label: 'Scheduled', statuses: ['draft', 'scheduled'] },
  { key: 'past', label: 'Past', statuses: ['ended', 'cancelled', 'expired'] }
];

const STATUS_STYLES = {
  draft: 'bg-gray-100 text-gray-700',
  scheduled: 'bg-blue-100 text-blue-700',
  active: 'bg-green-100 text-green-700',
  paused: 'bg-yellow-100 text-yellow-700',
  expired: 'bg-red-100 text-red-700',
  ended: 'bg-red-100 text-red-700',
  cancelled: 'bg-gray-100 text-gray-500'
};

function formatDiscount(promo) {
  if (promo.promotion_type === 'percentage') {
    return `-${parseFloat(promo.discount_value)}%`;
  }
  if (promo.promotion_type === 'fixed_amount') {
    return `-${formatCentsToDollars(parseFloat(promo.discount_value))}`;
  }
  return 'Free add-on';
}

function formatDateRange(start, end) {
  if (!start && !end) { return null; }
  const fmt = (d) => new Date(d).toLocaleDateString();
  if (start && end) { return `${fmt(start)} – ${fmt(end)}`; }
  if (start) { return `From ${fmt(start)}`; }
  return `Until ${fmt(end)}`;
}

function redemptionDisplay(promo) {
  const count = promo.current_redemptions ?? 0;
  const limit = promo.max_redemptions;
  return limit ? `${count} / ${limit}` : `${count} (Unlimited)`;
}

export default function PromotionListSection({ experienceId, onPromotionChange }) {
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('active');
  const [actionLoading, setActionLoading] = useState(null);
  const [confirmEndEarly, setConfirmEndEarly] = useState(null);

  const fetchPromotions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await getPromotions(experienceId);
      setPromotions(res.data || []);
    } catch (err) {
      setError(err.message || 'Failed to load promotions');
    } finally {
      setLoading(false);
    }
  }, [experienceId]);

  useEffect(() => {
    fetchPromotions();
  }, [fetchPromotions]);

  const handleStatusChange = async (promotionId, newStatus) => {
    try {
      setActionLoading(promotionId);
      await updatePromotion(experienceId, promotionId, { status: newStatus });
      await fetchPromotions();
      if (onPromotionChange) { onPromotionChange(); }
    } catch (err) {
      setError(err.message || `Failed to update promotion status`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleEndEarly = async (promotionId) => {
    setConfirmEndEarly(null);
    await handleStatusChange(promotionId, 'expired');
  };

  const handleDuplicate = async (promo) => {
    try {
      setActionLoading(promo.id);
      const payload = {
        name: `${promo.name} (copy)`,
        promotionType: promo.promotion_type,
        discountValue: parseFloat(promo.discount_value),
        isPublic: promo.is_public || false
      };

      if (promo.code) {
        payload.code = `${promo.code}_COPY`;
      }
      if (promo.promotion_type === 'percentage') {
        payload.discountUnit = 'percentage';
      } else if (promo.promotion_type === 'fixed_amount') {
        payload.discountUnit = 'cents';
      }
      if (promo.max_redemptions) { payload.maxRedemptions = promo.max_redemptions; }
      if (promo.max_redemptions_per_customer) { payload.maxRedemptionsPerCustomer = promo.max_redemptions_per_customer; }
      if (promo.min_party_size) { payload.minPartySize = promo.min_party_size; }
      if (promo.min_booking_amount_cents) { payload.minBookingValueCents = promo.min_booking_amount_cents; }

      await createPromotion(experienceId, payload);
      await fetchPromotions();
      if (onPromotionChange) { onPromotionChange(); }
    } catch (err) {
      setError(err.message || 'Failed to duplicate promotion');
    } finally {
      setActionLoading(null);
    }
  };

  const filteredPromotions = promotions.filter(p => {
    const tab = TABS.find(t => t.key === activeTab);
    return tab && tab.statuses.includes(p.status);
  });

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6" data-testid="promotion-list-section">
        <div className="flex items-center gap-2 mb-4">
          <Tag className="h-5 w-5 text-emerald-600" />
          <h3 className="text-lg font-semibold">Promotion Management</h3>
        </div>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6" data-testid="promotion-list-section">
      <div className="flex items-center gap-2 mb-4">
        <Tag className="h-5 w-5 text-emerald-600" />
        <h3 className="text-lg font-semibold">Promotion Management</h3>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md flex items-center gap-2 text-sm">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Tabs */}
      <div className="flex border-b mb-4" data-testid="promotion-tabs">
        {TABS.map(tab => {
          const count = promotions.filter(p => tab.statuses.includes(p.status)).length;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                activeTab === tab.key
                  ? 'border-emerald-600 text-emerald-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              data-testid={`tab-${tab.key}`}
            >
              {tab.label} ({count})
            </button>
          );
        })}
      </div>

      {/* Confirmation Dialog */}
      {confirmEndEarly && (
        <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg" data-testid="end-early-confirm">
          <p className="text-sm text-amber-800 font-medium mb-2">
            End this promotion early?
          </p>
          <p className="text-sm text-amber-700 mb-3">
            This will immediately expire the promotion. Customers will no longer be able to use it.
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => handleEndEarly(confirmEndEarly)}
              className="px-3 py-1.5 bg-red-600 text-white text-sm rounded-md hover:bg-red-700"
              data-testid="confirm-end-early-btn"
            >
              Yes, End Early
            </button>
            <button
              onClick={() => setConfirmEndEarly(null)}
              className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800"
              data-testid="cancel-end-early-btn"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Promotion List */}
      {filteredPromotions.length === 0 ? (
        <div className="text-center py-8 text-gray-500" data-testid="tab-empty-state">
          <Tag className="h-8 w-8 mx-auto mb-2 text-gray-300" />
          <p className="text-sm">No {activeTab} promotions</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="promotion-list">
          {filteredPromotions.map(promo => (
            <div
              key={promo.id}
              className="p-4 border rounded-lg hover:bg-gray-50"
              data-testid="promotion-card"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[promo.status] || 'bg-gray-100 text-gray-700'}`}>
                      {promo.status}
                    </span>
                    <span className="text-sm font-medium text-gray-900">{promo.name}</span>
                    <span className="text-sm text-emerald-600 font-medium" data-testid="promo-discount">
                      {formatDiscount(promo)}
                    </span>
                  </div>

                  <div className="flex items-center gap-4 text-xs text-gray-500 mt-1">
                    {promo.code && (
                      <span className="font-mono bg-gray-100 px-1.5 py-0.5 rounded">
                        {promo.code}
                      </span>
                    )}
                    <span data-testid="redemption-count">
                      Redemptions: {redemptionDisplay(promo)}
                    </span>
                    {formatDateRange(promo.booking_start_date, promo.booking_end_date) && (
                      <span>Booking: {formatDateRange(promo.booking_start_date, promo.booking_end_date)}</span>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center gap-1 ml-4" data-testid="promo-actions">
                  {actionLoading === promo.id ? (
                    <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                  ) : (
                    <>
                      {/* Activate — for draft/scheduled */}
                      {(promo.status === 'draft' || promo.status === 'scheduled') && (
                        <button
                          onClick={() => handleStatusChange(promo.id, 'active')}
                          className="p-1.5 text-green-600 hover:bg-green-50 rounded"
                          title="Activate"
                          data-testid="activate-btn"
                        >
                          <Play className="h-4 w-4" />
                        </button>
                      )}

                      {/* Pause — for active */}
                      {promo.status === 'active' && (
                        <button
                          onClick={() => handleStatusChange(promo.id, 'paused')}
                          className="p-1.5 text-yellow-600 hover:bg-yellow-50 rounded"
                          title="Pause"
                          data-testid="pause-btn"
                        >
                          <Pause className="h-4 w-4" />
                        </button>
                      )}

                      {/* Resume — for paused */}
                      {promo.status === 'paused' && (
                        <button
                          onClick={() => handleStatusChange(promo.id, 'active')}
                          className="p-1.5 text-green-600 hover:bg-green-50 rounded"
                          title="Resume"
                          data-testid="resume-btn"
                        >
                          <RotateCcw className="h-4 w-4" />
                        </button>
                      )}

                      {/* End Early — for active */}
                      {promo.status === 'active' && (
                        <button
                          onClick={() => setConfirmEndEarly(promo.id)}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                          title="End Early"
                          data-testid="end-early-btn"
                        >
                          <Square className="h-4 w-4" />
                        </button>
                      )}

                      {/* Edit — for scheduled only (draft/scheduled) */}
                      {(promo.status === 'draft' || promo.status === 'scheduled') && (
                        <button
                          onClick={() => {/* Edit handled by parent via onPromotionChange */}}
                          className="p-1.5 text-gray-600 hover:bg-gray-100 rounded"
                          title="Edit"
                          data-testid="edit-btn"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                      )}

                      {/* Duplicate — available for all */}
                      <button
                        onClick={() => handleDuplicate(promo)}
                        className="p-1.5 text-gray-600 hover:bg-gray-100 rounded"
                        title="Duplicate"
                        data-testid="duplicate-btn"
                      >
                        <Copy className="h-4 w-4" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
